import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from '@angular/core';
import { UserManagementDetailRecord, UserRole } from "../pages/user-management/user-management-record.model";
import { ConfigService } from "@ph-core/services/config/config.service";
import { catchError, Observable, of } from "rxjs";
import {
  DealerDefaultsResponse,
  ResetDealerDefaultsResponse,
  SaveDealerDefaultsRequest,
} from "@ph-model/api/response/dealer-defaults.response.model";
import { EmulateDealerResponse } from "@ph-model/api/response/emulate-dealer.response";
import { ResetUserDefaultsResponse } from "@ph-model/api/response/reset-user-defaults.response.model";
import { UserProfile } from "../models/user.model";

@Injectable({ providedIn: 'root' })
export class UserService {
  private http = inject(HttpClient);
  private environment = inject(ConfigService).environment;
  private apiUrl = this.environment.apiUrl;

  getAdminRoles(): Observable<UserRole[]> {
    return this.http.get<UserRole[]>(`${this.apiUrl}/dealer-admin/roles`)
  }

  getEmulationDealers(): Observable<EmulateDealerResponse[]> {
    return this.http.get<EmulateDealerResponse[]>(`${this.apiUrl}/dealer-admin/emulation-dealers`)
  }

  getUserDetail(id: number): Observable<UserManagementDetailRecord> {
    return this.http.get<UserManagementDetailRecord>(`${this.apiUrl}/dealer-admin/users/${id}`)
  }

  verifyUsername(username: string): Observable<{ usernameAvailable: boolean } | null> {
    return this.http.get<{ usernameAvailable: boolean }>(`${this.apiUrl}/dealer-admin/users/verify-username`, {
      params: { username },
    }).pipe(catchError(() => of(null)));
  }

  resetPassword(userId: number): Observable<{ password: string } | null> {
    return this.http.post<{ password: string }>(
      `${this.apiUrl}/dealer-admin/users/${userId}/reset-password`,
      {}
    ).pipe(catchError(() => of(null)));
  }

  resetOktaMFA(userId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/dealer-admin/users/${userId}/reset-mfa`, {});
  }

  createUser(user: UserProfile): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/dealer-admin/users`, user);
  }

  updateUser(user: UserProfile, userId: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/dealer-admin/users/${userId}`, user);
  }

  resetUserDefaults(): Observable<ResetUserDefaultsResponse> {
    return this.http.post<ResetUserDefaultsResponse>(`${this.apiUrl}/userdefaultadd/reset`, {});
  }

  getDealerDefaults(): Observable<DealerDefaultsResponse> {
    return this.http.get<DealerDefaultsResponse>(`${this.apiUrl}/dealer-defaults`);
  }

  saveDealerDefaults(payload: SaveDealerDefaultsRequest): Observable<{ status: boolean }> {
    return this.http.post<{ status: boolean }>(`${this.apiUrl}/dealer-defaults`, payload);
  }

  resetDealerDefaults(): Observable<ResetDealerDefaultsResponse> {
    return this.http.post<ResetDealerDefaultsResponse>(`${this.apiUrl}/dealer-defaults/reset`, {});
  }
}